import { EquipmentProofLink } from "@/components/oprema/EquipmentProofLink";
import "@/components/oprema/Oprema.css";
import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, Select } from "@/components/ui/design-system";
import { Feedback, useResource } from "@/components/workflow/shared";
import {
  equipmentApi,
  issueUrl,
  type SiteStock,
  type StockMovement,
} from "@/lib/oprema";
import { dohvatiGradilisniSpisak } from "@/lib/siteDossier";
import { danas } from "@/lib/dan";
import { datum } from "@/lib/format";
import { errorMessage } from "@/lib/api";
import { ReturnedHandoverNotice } from "./HandoverNotice";

export function SiteEquipment({ siteId }: { siteId: string }) {
  const { can } = useAuth();
  const location = useLocation();
  const read = can("oprema", "read");
  const create = can("oprema", "create");
  const update = can("oprema", "update");
  const resource = useResource(
    `${siteId}:${read}`,
    useCallback(
      async (signal) => {
        if (!read) return null;
        const [overview, stock] = await Promise.all([
          equipmentApi.overview(siteId, signal),
          equipmentApi.stock(siteId, signal),
        ]);
        return { overview, stock };
      },
      [siteId, read],
    ),
  );
  const [movement, setMovement] = useState<{
    item: SiteStock[number];
    kind: StockMovement["vrsta_prometa"];
  } | null>(null);
  if (!read) return null;
  const data = resource.data;
  return (
    <div className="avm-equipment space-y-4 py-4">
      <ReturnedHandoverNotice onUndone={resource.reload} />
      {can("nabava", "create") && <Link className="inline-flex min-h-11 items-center rounded-lg border px-4 font-semibold" to={`/nabava/nova?gradiliste=${siteId}&iz_opreme=1`}>Naruči što fali</Link>}
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      {resource.loading && <p role="status">Učitavanje opreme…</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(
              [
                [
                  "Ističe ≤ 14 dana",
                  data.overview.plocice.istice_14,
                  "border-red-200 bg-red-50 text-red-950",
                ],
                [
                  "Fali veličina",
                  data.overview.plocice.fali_velicina,
                  "border-amber-200 bg-amber-50 text-amber-950",
                ],
                [
                  "Nije popisano",
                  data.overview.plocice.nije_popisano,
                  "border-amber-200 bg-amber-50 text-amber-950",
                ],
                [
                  "Rukavice ovaj mjesec",
                  data.overview.plocice.rukavice_mjesec,
                  "border-blue-200 bg-blue-50 text-blue-950",
                ],
                [
                  "Bez fotke",
                  data.overview.plocice.bez_fotke,
                  data.overview.plocice.bez_fotke > 0
                    ? "border-red-200 bg-red-50 text-red-950"
                    : "border-green-200 bg-green-50 text-green-950",
                ],
              ] as const
            ).map(([title, count, tone]) => (
              <div key={title} className={`rounded-lg border p-4 ${tone}`}>
                <p className="text-sm">{title}</p>
                <p className="text-2xl font-bold">{count}</p>
              </div>
            ))}
          </div>
          <section className="space-y-3 rounded-lg border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Treba riješiti</h2>
              {create && (
                <Link
                  className="inline-flex min-h-11 items-center underline"
                  to={`/gradilista/${siteId}/oprema/pocetno-stanje`}
                >
                  Početno stanje
                </Link>
              )}
            </div>
            {!data.overview.treba_rijesiti.length && <p>✓ Sve je riješeno.</p>}
            <ul className="divide-y">
              {data.overview.treba_rijesiti.map((item, index) => {
                const destination =
                  item.akcija === "izdaj"
                    ? issueUrl(item.radnik_id, siteId)
                    : item.akcija === "popisi"
                      ? `/gradilista/${siteId}/oprema/pocetno-stanje?radnik=${item.radnik_id}`
                      : `/zaposlenici/${item.radnik_id}?tab=znr#${item.akcija === "upisi_velicinu" ? "velicine" : "ozo"}`;
                const permitted =
                  item.akcija === "izdaj" || item.akcija === "popisi"
                    ? create
                    : item.akcija === "upisi_velicinu"
                      ? can("radnik", "update")
                      : can("znr", "update");
                return (
                  <li
                    key={`${item.radnik_id}:${index}`}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <p className="font-semibold">✗ {item.ime}</p>
                      <p>{item.poruka}</p>
                    </div>
                    {permitted && (
                      <Link
                        className="inline-flex min-h-11 items-center rounded border px-4 underline"
                        to={destination}
                        state={{
                          from: `${location.pathname}${location.search}`,
                        }}
                      >
                        {
                          {
                            izdaj: "Izdaj",
                            upisi_velicinu: "Upiši veličinu",
                            uredi: "Uredi",
                            popisi: "Popiši",
                          }[item.akcija]
                        }
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
          <section className="space-y-3 rounded-lg border bg-white p-4">
            <h2 className="text-lg font-semibold">Zaliha gradilišta</h2>
            {!data.stock.length && <p>Nema evidentirane zalihe.</p>}
            {data.stock.map((item) => (
              <article
                key={item.vrsta_ozo_id}
                className="flex flex-wrap items-center justify-between gap-3 border-b py-3"
              >
                <p>
                  <strong>{item.naziv}</strong> · {item.stanje} kom
                </p>
                {update && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      className="min-h-11"
                      onClick={() => setMovement({ item, kind: "izdano" })}
                    >
                      Izdaj / Vrati
                      <span className="sr-only">: {item.naziv}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="min-h-11"
                      onClick={() => setMovement({ item, kind: "primitak" })}
                    >
                      Primljeno<span className="sr-only">: {item.naziv}</span>
                    </Button>
                  </div>
                )}
              </article>
            ))}
            <h3 className="font-semibold">Nevraćeno danas</h3>
            {!data.stock.flatMap((stock) =>
              stock.nevraceno_danas.map((item) => ({
                ...item,
                naziv: stock.naziv,
              })),
            ).length ? (
              <p>✓ Sve je vraćeno.</p>
            ) : (
              <ul>
                {data.stock
                  .flatMap((stock) =>
                    stock.nevraceno_danas.map((item) => ({
                      ...item,
                      naziv: stock.naziv,
                    })),
                  )
                  .map((item, index) => (
                    <li key={`${item.radnik_id}:${index}`} className="py-2">
                      {item.ime} · {item.naziv} · {item.kolicina} kom
                    </li>
                  ))}
              </ul>
            )}
          </section>
          <details className="rounded-lg border bg-white p-4">
            <summary className="min-h-11 cursor-pointer font-semibold">
              Nedavne primopredaje
            </summary>
            <ul className="divide-y">
              {data.overview.nedavno.map((item) => (
                <li key={item.id} className="py-3">
                  {datum(item.datum)} · {item.ime} · {item.broj_stavki} stavki ·{" "}
                  <span
                    className={
                      item.ima_fotku ? "text-green-800" : "text-red-800"
                    }
                  >
                    {item.ima_fotku ? "✓ Ima fotku" : "✗ Bez fotke"}
                  </span>
                  {item.dokument_id && (can("dokument", "read") || can("oprema", "read")) && (
                    <EquipmentProofLink
                      className="ml-3 inline-flex min-h-11 items-center underline"
                      workerId={item.radnik_id} documentId={item.dokument_id}
                    >
                      Otvori potvrdu
                    </EquipmentProofLink>
                  )}
                </li>
              ))}
            </ul>
          </details>
          <details className="rounded-lg border bg-white p-4">
            <summary className="min-h-11 cursor-pointer font-semibold">
              Potrošnja rukavica po radniku
            </summary>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className="p-3">Radnik</th>
                    <th className="p-3">Ovaj mjesec</th>
                    <th className="p-3">Prošli mjesec</th>
                  </tr>
                </thead>
                <tbody>
                  {data.overview.potrosnja_rukavica.map((item) => (
                    <tr key={item.radnik_id}>
                      <td className="p-3">{item.ime}</td>
                      <td className="p-3">{item.tekuci_mjesec}</td>
                      <td className="p-3">{item.prosli_mjesec}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
      {movement && (
        <StockDialog
          siteId={siteId}
          item={movement.item}
          initialKind={movement.kind}
          onClose={() => setMovement(null)}
          onSaved={() => {
            setMovement(null);
            resource.reload();
          }}
        />
      )}
    </div>
  );
}
function StockDialog({
  siteId,
  item,
  initialKind,
  onClose,
  onSaved,
}: {
  siteId: string;
  item: SiteStock[number];
  initialKind: StockMovement["vrsta_prometa"];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { can } = useAuth();
  const [worker, setWorker] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const roster = useResource(
    `${siteId}:${initialKind}`,
    useCallback(
      async (signal) =>
        initialKind === "primitak"
          ? []
          : (await dohvatiGradilisniSpisak(siteId, danas(), signal)).workers,
      [siteId, initialKind],
    ),
  );
  const count = Number(quantity);
  const valid =
    Number.isSafeInteger(count) &&
    count > 0 &&
    (initialKind === "primitak" || Boolean(worker));
  async function submit(kind: StockMovement["vrsta_prometa"]) {
    if (!valid || busy || !can("oprema", "update")) return;
    setBusy(true);
    setError("");
    try {
      await equipmentApi.move(siteId, {
        vrsta_ozo_id: item.vrsta_ozo_id,
        vrsta_prometa: kind,
        kolicina: count,
        ...(kind !== "primitak" ? { radnik_id: worker } : {}),
        napomena: note.trim() || undefined,
      });
      onSaved();
    } catch (cause) {
      setError(errorMessage(cause, "Promet zalihe nije spremljen."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={`${initialKind === "primitak" ? "Primljeno" : "Izdaj / Vrati"} · ${item.naziv}`}
      onClose={() => {
        if (!busy) onClose();
      }}
      actions={
        <>
          {initialKind === "primitak" ? (
            <Button
              className="min-h-11"
              disabled={!valid || busy}
              onClick={() => void submit("primitak")}
            >
              Primljeno
            </Button>
          ) : (
            <>
              <Button
                className="min-h-11"
                disabled={!valid || busy || count > item.stanje}
                onClick={() => void submit("izdano")}
              >
                Izdaj
              </Button>
              <Button
                className="min-h-11"
                variant="outline"
                disabled={!valid || busy}
                onClick={() => void submit("vraceno")}
              >
                Vrati
              </Button>
            </>
          )}
          <Button
            className="min-h-11"
            variant="outline"
            disabled={busy}
            onClick={onClose}
          >
            Odustani
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Feedback error={error || roster.error} />
        {roster.loading && initialKind !== "primitak" && (
          <p role="status">Učitavanje popisa radnika…</p>
        )}
        {initialKind !== "primitak" && (
          <label className="block">
            Radnik
            <Select
              className="min-h-11 w-full"
              value={worker}
              disabled={busy}
              onChange={(event) => setWorker(event.target.value)}
            >
              <option value="">Odaberite radnika</option>
              {roster.data?.map((person) => (
                <option key={person.radnik_id} value={person.radnik_id}>
                  {person.full_name}
                </option>
              ))}
            </Select>
          </label>
        )}
        <label className="block">
          Količina
          <Input
            className="min-h-11"
            type="number"
            min={1}
            step={1}
            value={quantity}
            disabled={busy}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </label>
        <label className="block">
          Napomena
          <Input
            className="min-h-11"
            value={note}
            maxLength={2000}
            disabled={busy}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
      </div>
    </Dialog>
  );
}
